import * as React from 'react';
import cx from 'classnames';
import px from 'prop-types';
import { BREAKPOINTS } from 'Common/utils/Viewport';
import * as types from 'Common/types';
import { useBEM, useViewport } from 'Common/hooks';
import { InfiniteScroll } from 'Common/components/ui';
import { CatalogAltQuickview } from 'Common/components/catalog';
import GridCard from './GridCard';

export const DEFAULT_CARD_SIZE = 360;

export function maxRowSize(bp, colSize = DEFAULT_CARD_SIZE) {
    const min = BREAKPOINTS[bp] || 0;

    return Math.max(1, Math.floor(min / (colSize + 24)));
}

export function calculateQuickviewIndex(quickview, rowSize) {
    return quickview ? Math.ceil((quickview.idx + 1) / rowSize) * rowSize - 1 : -1;
}

export default function CatalogExploreGrid({
    checkoutPageLink,
    className,
    colSize = DEFAULT_CARD_SIZE,
    googleRecaptchaV3Sitekey,
    gtmListValue,
    inputStatusTooltip,
    isUserAuthenticated,
    noBuyNow,
    NotifyMe,
    notifyProps,
    onAddItemToCart,
    onAddMultipleToCart,
    onLoadMore,
    pageInfo = {},
    products = [],
    Quickview = CatalogAltQuickview,
    totalQty = 0,
    useRecaptcha,
}) {
    const viewPort = useViewport();
    const handler = React.useRef();
    const [hide, setHide] = React.useState(false);
    const [quickview, setQuickview] = React.useState(null);
    const isDoneLoading = React.useMemo(() => pageInfo.page * pageInfo.pageSize >= pageInfo.total, [pageInfo]);
    const [b, em] = useBEM('CatalogExploreGrid', className);
    const rowSize = React.useMemo(() => maxRowSize(viewPort.name, colSize), [viewPort.name, colSize]);
    const quickviewIdx = React.useMemo(
        () => Math.min(calculateQuickviewIndex(quickview, rowSize), products.length - 1),
        [rowSize, quickview, products]
    );

    const closeQuickview = React.useCallback(() => {
        setHide(true);
        clearTimeout(handler.current);
        handler.current = setTimeout(() => {
            setQuickview(null);
        }, 310);
    }, []);

    const onOpenQuickview = React.useCallback(
        (idx, product) => (e) => {
            e.stopPropagation();
            e.preventDefault();
            setHide(false);
            clearTimeout(handler.current);
            setQuickview({ idx, product });
        },
        []
    );

    const onCloseQuickview = React.useCallback(
        (e) => {
            e.stopPropagation();
            e.preventDefault();
            closeQuickview();
        },
        [closeQuickview]
    );

    React.useEffect(() => () => clearTimeout(handler.current), []);

    return (
        <div className={b} style={{ '--grid-col-size': `${colSize}px` }}>
            <InfiniteScroll onLoad={onLoadMore} useLoadIndicator={true} isDoneLoading={isDoneLoading}>
                <div className={em('grid')} data-row-size={rowSize}>
                    {products.map((x, i) => (
                        <React.Fragment key={`ceg-${x.Code}`}>
                            <GridCard
                                className={em('grid__card')}
                                active={quickview?.idx === i}
                                onOpenQuickview={onOpenQuickview(i, x)}
                                onCloseQuickview={onCloseQuickview}
                                product={x}
                            />
                            {quickviewIdx === i ? (
                                <Quickview
                                    className={cx('CatalogListCard', em('grid__quickview'), {
                                        [em('grid__quickview', 'hide')]: hide,
                                    })}
                                    product={quickview.product}
                                    isMobile={rowSize === 1}
                                    NotifyMe={NotifyMe}
                                    notifyProps={notifyProps}
                                    onClose={closeQuickview}
                                    gtmListValue={gtmListValue}
                                    isUserAuthenticated={isUserAuthenticated}
                                    checkoutPageLink={checkoutPageLink}
                                    useRecaptcha={useRecaptcha}
                                    googleRecaptchaV3Sitekey={googleRecaptchaV3Sitekey}
                                    onAddItemToCart={onAddItemToCart}
                                    onAddMultipleToCart={onAddMultipleToCart}
                                    inputStatusTooltip={inputStatusTooltip}
                                    noBuyNow={noBuyNow}
                                    totalQty={totalQty}
                                />
                            ) : null}
                        </React.Fragment>
                    ))}
                </div>
            </InfiniteScroll>
        </div>
    );
}

CatalogExploreGrid.propTypes = {
    checkoutPageLink: px.string,
    className: px.string,
    colSize: px.number,
    googleRecaptchaV3Sitekey: px.string,
    gtmListValue: px.string,
    inputStatusTooltip: px.oneOfType([px.string, px.bool]),
    isUserAuthenticated: px.bool,
    noBuyNow: px.bool,
    NotifyMe: px.elementType,
    notifyProps: px.object,
    onAddItemToCart: px.func,
    onAddMultipleToCart: px.func,
    onLoadMore: px.func,
    pageInfo: types.PageInfo,
    products: px.arrayOf(types.Product),
    Quickview: px.elementType,
    totalQty: px.number,
    useRecaptcha: px.bool,
};
